/* eslint-disable camelcase */
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/styles';
import { format, isValid, parseISO } from 'date-fns';
import { Field, Formik } from 'formik';
import React, { useState } from 'react';

import { Colors } from 'src/common/ui/base';
import { PrimaryButton } from 'src/components/general/PrimaryButton';
import { SecondaryButton } from 'src/components/general/SecondaryButton';
import {
  Staff_LabResultInvalidReason as LabResultInvalidReason,
  Staff_LabResultStatus as LabResultStatus,
} from 'src/generated/gql/graphql';
import { PatientSubmittedTestResult } from 'src/labs/types';
import { BooleanControl } from 'src/nightingale/components/ChartProperty/controls/Boolean/BooleanControl';
import { TextControl } from 'src/nightingale/components/ChartProperty/controls/Text/TextControl';

const invalidReasonOptions = [
  { label: 'Poor image quality/lighting', value: LabResultInvalidReason.TooBlurry },
  { label: `Image doesn't contain POCT cup`, value: LabResultInvalidReason.CupMissing },
  { label: 'Cup appears empty', value: LabResultInvalidReason.CupEmpty },
  { label: 'Cup is not Boulder-issued', value: LabResultInvalidReason.WrongCup },
  {
    label: 'Label or sticker not removed or blocking results',
    value: LabResultInvalidReason.LabelBlockingResults,
  },
  { label: 'Temperature strip/photo is missing', value: LabResultInvalidReason.TempStripMissing },
  { label: 'Other (describe in notes)', value: LabResultInvalidReason.Other },
];

const poctFormValidation = values => {
  const errors: Record<string, string> = {};

  // verified is required
  if (!(values.verified === true || values.verified === false)) {
    errors.verified = 'Must select a verified value';
    return errors;
  }

  // reason is required when verified is false
  if (values.verified === false && !values.invalidReason) {
    errors.invalidReason = 'Must select a reason for invalid result';
    return errors;
  }

  // bup positive is required when verified is true
  if (values.verified === true && !(values.bupPositive === true || values.bupPositive === false)) {
    errors.bupPositive = 'Must select a bup positive value';
    return errors;
  }

  return errors;
};

export const EditLabResultFormV2: React.FC<{
  images: PatientSubmittedTestResult['images'];
  item: Partial<PatientSubmittedTestResult['result']>;
  onCancel: () => void;
  onSaveAsDraft: (values: Partial<PatientSubmittedTestResult['result']>) => Promise<void>;
  onSignAndResult: (values: Partial<PatientSubmittedTestResult['result']>) => Promise<void>;
}> = ({ images, item, onCancel, onSaveAsDraft, onSignAndResult }) => {
  const classes = useStyles();
  const isEditable = item.status !== LabResultStatus.Resulted;
  const isIncomplete = item.status === LabResultStatus.Incomplete;
  const isResultSigned = !!item.signedBy;

  const [isConfirmingSave, setIsConfirmingSave] = useState(false);
  const [saveConfirmDelay, setSaveConfirmDelay] = useState(false);

  return (
    <>
      <figure className={classes.images}>
        {images.map((image, index) => (
          <img
            key={image.url}
            className={classes.resultImage}
            src={image.url}
            alt={`Lab result upload #${index + 1}`}
          />
        ))}
      </figure>

      <Formik
        validate={poctFormValidation}
        initialValues={item}
        onSubmit={async (values, actions) => {
          onSignAndResult(values).finally(() => {
            actions.setSubmitting(false);
          });
        }}
      >
        {({
          handleSubmit,
          isSubmitting,
          dirty,
          errors,
          values,
          validateForm,
          isValid: isFormValid,
        }) => {
          const notesChanged = values.notes !== item.notes;
          return (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Field
                label="Result valid?"
                name="verified"
                isEditable={isEditable}
                component={FormBooleanControl}
              />
              {errors.verified && (
                <span className={classes.requiredFieldError}>{errors.verified}</span>
              )}
              {values.verified && (
                <Field
                  label="Bup positive?"
                  name="bupPositive"
                  isEditable={isEditable}
                  component={FormBooleanControl}
                />
              )}
              {errors.bupPositive && (
                <span className={classes.requiredFieldError}>{errors.bupPositive}</span>
              )}
              {values.verified === false && (
                <Field
                  label="Why aren't the results valid?"
                  helpText="Select the primary issue"
                  name="invalidReason"
                  isEditable={isEditable}
                  component={FormRadioControl}
                />
              )}
              {errors.invalidReason && (
                <span className={classes.requiredFieldError}>{errors.invalidReason}</span>
              )}
              <Field label="Additional Notes" name="notes" multiline component={FormTextControl} />
              {!isEditable && (
                <span className={classes.resultedByText}>{buildResultedByText(item)}</span>
              )}

              <span className={classes.buttons}>
                <div>
                  {isConfirmingSave && (
                    <span className={classes.confirmationText}>Are you sure?</span>
                  )}
                  <SecondaryButton
                    onClick={isConfirmingSave ? () => setIsConfirmingSave(false) : () => onCancel()}
                  >
                    Cancel
                  </SecondaryButton>
                  {isConfirmingSave && (
                    <PrimaryButton
                      className={classes.button}
                      disabled={isSubmitting || saveConfirmDelay}
                      type="submit"
                    >
                      Confirm
                    </PrimaryButton>
                  )}
                  {isIncomplete && !isResultSigned && !isConfirmingSave && (
                    <>
                      <PrimaryButton
                        className={classes.button}
                        onClick={() => {
                          const filteredValues = {
                            ...values,
                            bupPositive: values.verified ? values.bupPositive : null,
                          };
                          onSaveAsDraft(filteredValues);
                        }}
                        disabled={isSubmitting || !dirty}
                      >
                        Save as Draft
                      </PrimaryButton>
                      <PrimaryButton
                        className={classes.button}
                        disabled={isSubmitting || !isFormValid}
                        onClick={async () => {
                          const formErrors = await validateForm();
                          if (Object.keys(formErrors).length > 0) return;
                          setSaveConfirmDelay(true);
                          setTimeout(() => {
                            setSaveConfirmDelay(false);
                          }, 350);
                          setIsConfirmingSave(true);
                        }}
                      >
                        Sign and Result
                      </PrimaryButton>
                    </>
                  )}
                  {isResultSigned && notesChanged && !isConfirmingSave && (
                    <PrimaryButton
                      className={classes.button}
                      onClick={() => {
                        const filteredValues = {
                          ...values,
                          bupPositive: values.verified ? values.bupPositive : null,
                        };
                        onSaveAsDraft(filteredValues);
                      }}
                      disabled={isSubmitting || !dirty}
                    >
                      Save
                    </PrimaryButton>
                  )}
                </div>
              </span>
              {isIncomplete && !isResultSigned && !isConfirmingSave && (
                <span className={classes.helpTextContainer}>
                  <span className={classes.confirmationText}>Signing prevents further edits</span>
                </span>
              )}
            </form>
          );
        }}
      </Formik>
    </>
  );
};

const FormBooleanControl = ({ field, form, label, isEditable }) => (
  <BooleanControl
    label={label}
    name={field.name}
    onChangeValue={newVal => {
      form.setFieldValue(field.name, newVal);
    }}
    value={field.value}
    disabled={!isEditable}
  />
);

const FormTextControl = ({ field, form, label }) => (
  <TextControl
    label={label}
    multiline
    name={field.name}
    onChangeValue={newVal => {
      form.setFieldValue(field.name, newVal);
    }}
    value={field.value || ''}
  />
);

const FormRadioControl = ({ field, form, label, helpText, isEditable }) => {
  const styles = useStyles({});
  return (
    <div className={styles.radioControlContainer}>
      <InputLabel
        classes={{
          root: styles.label,
        }}
        shrink
      >
        <span>{label}</span>
      </InputLabel>
      {helpText && <span className={styles.helpText}>{helpText}</span>}
      <RadioGroup
        value={field.value}
        onChange={event => {
          form.setFieldValue(field.name, event.target.value);
        }}
      >
        {invalidReasonOptions.map(optionProps => (
          <FormControlLabel
            key={optionProps.label}
            control={<Radio />}
            {...optionProps}
            disabled={!isEditable}
          />
        ))}
      </RadioGroup>
    </div>
  );
};

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 30,
    '& .MuiFormControl-root': {
      marginBottom: 16,
    },
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 20,
  },
  button: {
    marginLeft: 8,
  },
  images: {
    alignItems: 'center',
    columnGap: 20,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: '0 0 20px',
    rowGap: 20,
  },
  resultImage: {
    display: 'block',
    height: 'auto',
    margin: 0,
    marginBottom: '20px',
    maxWidth: '90vh',
    width: '100%',
  },
  label: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontWeight: 600,
    fontSize: 10,
    lineHeight: '14.5px',
    letterSpacing: `.12em`,
    '&, &.Mui-error, &.Mui-focused': {
      color: Colors.Gray6,
    },
    textTransform: 'uppercase',
    transform: 'scale(1)',
    position: 'static',
  },
  helpText: {
    fontSize: 12,
    fontFamily: '"Nunito", "Nunito Sans"',
    fontWeight: 600,
    fontStyle: 'italic',
    '&, &.Mui-error, &.Mui-focused': {
      color: Colors.Gray6,
    },
    transform: 'scale(1)',
    position: 'static',
  },
  radioControlContainer: {
    marginTop: 4,
  },
  resultedByText: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 13,
    fontStyle: 'italic',
  },
  requiredFieldError: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 13,
    fontStyle: 'italic',
    color: Colors.Red2,
    marginBottom: 12,
  },
  confirmationText: {
    fontFamily: '"Nunito", "Nunito Sans"',
    fontSize: 12,
    fontStyle: 'italic',
    flexDirection: 'row-reverse',
    marginRight: 12,
  },
  helpTextContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 4,
    marginRight: -4,
  },
});

/**
 * Builds a string of the form "Resulted by {firstName} {lastName} on {date/time}", but leaving
 * out any data this is not populated on the input `item`.
 */
function buildResultedByText(item: Partial<PatientSubmittedTestResult['result']>): string {
  let text = 'Resulted';

  if (item.signedBy?.firstName || item.signedBy?.lastName) {
    text += ` by ${item.signedBy?.firstName || '(first name unknown)'} ${
      item.signedBy?.lastName || '(last name unknown)'
    }`;
  }

  if (item.signedAt) {
    const signedAt = parseISO(item.signedAt);
    if (isValid(signedAt)) {
      text += ` on ${format(signedAt, 'PPPppp')}`;
    }
  }

  return text;
}
